import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Image as ImageIcon, X } from "lucide-react";
import { assetUrl } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

/** Strip the parenthetical meaning from a degree, keeping only the abbreviation.
 * e.g. "MPH, MD (Master of Public Health, Medical Doctor)" -> "MPH, MD" */
export function cleanDegree(deg) {
  if (!deg) return "";
  return String(deg).split("(")[0].trim().replace(/,\s*$/, "").trim();
}

export function articleMainAuthor(article) {
  const a = (article?.authors || [])[0];
  if (!a) return { name: "—", photo: "" };
  const name = [a.first_name, a.last_name].filter(Boolean).join(" ");
  const deg = cleanDegree(a.academic_degree);
  return { name: deg ? `${name}, ${deg}` : name, photo: a.photo_url };
}

export function ArticleMetadata({ article }) {
  const parts = [];
  if (article.volume) parts.push(`Vol. ${article.volume}`);
  if (article.issue) parts.push(`Issue ${article.issue}`);
  if (article.published_date) parts.push(article.published_date);
  else if (article.year) parts.push(article.year);
  return (
    <div className="text-xs font-sans text-gray-500 flex items-center flex-wrap gap-x-2 gap-y-1">
      {parts.map((p, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="text-[#2BA6A6]">•</span>}
          <span>{p}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

export function AuthorAvatar({ photo, name, size = 40 }) {
  const initials = (name || "?")
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  if (photo) {
    return (
      <img
        src={assetUrl(photo)}
        alt={name}
        className="rounded-full object-cover shrink-0 border border-gray-100"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-[#F5F7FA] text-[#001F3F] flex items-center justify-center font-sans font-semibold text-xs border border-gray-100 shrink-0"
      style={{ width: size, height: size }}
    >
      {initials}
    </div>
  );
}

export function FeaturedCard({ article }) {
  const main = articleMainAuthor(article);
  return (
    <Link
      to={`/articles/${article.slug}`}
      data-testid={`featured-card-${article.slug}`}
      className="bg-white border border-gray-100 p-7 flex flex-col justify-between min-h-[300px] hover:shadow-lg transition-all duration-300 group"
    >
      <div>
        <div className="text-[11px] font-sans tracking-[0.2em] uppercase font-semibold text-[#2BA6A6] mb-4">
          {article.article_type}
        </div>
        <h3 className="font-serif text-xl text-[#001F3F] leading-snug group-hover:text-[#2BA6A6] transition-colors">
          {article.title}
        </h3>
      </div>
      <div className="mt-6 pt-5 border-t border-gray-100">
        <div className="flex items-center gap-3 mb-3">
          <AuthorAvatar photo={main.photo} name={main.name} size={36} />
          <div className="font-sans text-sm text-[#001F3F]">{main.name}</div>
        </div>
        <ArticleMetadata article={article} />
      </div>
    </Link>
  );
}

export function ArticleRow({ article }) {
  const { t } = useI18n();
  const main = articleMainAuthor(article);
  const [zoom, setZoom] = useState(false);
  return (
    <div
      data-testid={`article-row-${article.slug}`}
      className="flex items-start md:items-center gap-5 py-6 border-b border-gray-100"
    >
      <AuthorAvatar photo={main.photo} name={main.name} size={48} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-sans tracking-[0.2em] uppercase font-semibold text-[#2BA6A6] mb-1.5">
          {article.article_type}
        </div>
        <Link
          to={`/articles/${article.slug}`}
          className="font-serif text-base md:text-lg text-[#001F3F] leading-snug hover:text-[#2BA6A6] transition-colors block"
        >
          {article.title}
        </Link>
        <div className="mt-2 text-xs font-sans text-gray-500 flex items-center flex-wrap gap-x-2 gap-y-1">
          <span>{main.name}</span>
          {article.volume && <><span className="text-[#2BA6A6]">•</span><span>Vol. {article.volume}, Issue {article.issue}</span></>}
          {article.published_date && <><span className="text-[#2BA6A6]">•</span><span>{article.published_date}</span></>}
          {!article.published_date && article.year && <><span className="text-[#2BA6A6]">•</span><span>{article.year}</span></>}
        </div>
        {article.main_specialty && (
          <div
            className="mt-1.5 text-[11px] font-sans font-semibold uppercase tracking-[0.12em] text-[#2BA6A6]"
            data-testid={`article-specialty-${article.slug}`}
          >
            {article.main_specialty}
          </div>
        )}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {article.pdf_url && (
          <a
            href={assetUrl(article.pdf_url)}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 text-[#2BA6A6] text-xs font-sans uppercase tracking-wider hover:text-[#001F3F] transition-colors"
            data-testid={`row-view-pdf-${article.slug}`}
          >
            <FileText className="w-4 h-4" />
            {t("article.view_pdf")}
          </a>
        )}
        <Link
          to={`/articles/${article.slug}`}
          className="hidden md:inline-flex items-center gap-2 text-[#001F3F] text-xs font-sans uppercase tracking-wider border border-[#001F3F] px-4 py-2 hover:bg-[#001F3F] hover:text-white transition-colors"
          data-testid={`row-details-${article.slug}`}
        >
          {t("article.details")}
        </Link>
        {article.visual_abstract_url && (
          <button
            type="button"
            onClick={() => setZoom(true)}
            className="hidden md:flex flex-col items-center gap-1 group"
            data-testid={`row-visual-abstract-${article.slug}`}
            aria-label={t("article.visual_abstract")}
            title={t("article.visual_abstract")}
          >
            <img
              src={assetUrl(article.visual_abstract_url)}
              alt={t("article.visual_abstract")}
              className="w-16 h-16 object-cover border border-gray-200 group-hover:border-[#2BA6A6] transition-colors"
            />
            <span className="text-[9px] font-sans uppercase tracking-wider text-[#001F3F] group-hover:text-[#2BA6A6]">
              {t("article.visual_abstract")}
            </span>
          </button>
        )}
      </div>

      {zoom && article.visual_abstract_url && (
        <div
          className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setZoom(false)}
          data-testid="visual-abstract-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setZoom(false); }}
            className="absolute top-6 right-6 text-white hover:text-[#2BA6A6]"
            aria-label="Close"
          >
            <X className="w-8 h-8" />
          </button>
          <figure className="max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <img
              src={assetUrl(article.visual_abstract_url)}
              alt={t("article.visual_abstract")}
              className="max-h-[85vh] w-auto bg-white border border-white/20"
            />
            <figcaption className="text-white/80 text-sm font-sans mt-3 text-center">
              {article.title}
            </figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}

export function SectionTitle({ children, subtitle }) {
  return (
    <div className="text-center mb-12">
      <h2 className="font-serif text-3xl md:text-4xl text-[#001F3F] tracking-tight">{children}</h2>
      <div className="mt-4 mx-auto w-12 h-[2px] bg-[#2BA6A6]"></div>
      {subtitle && (
        <p className="mt-4 text-sm font-sans text-gray-500 max-w-2xl mx-auto">{subtitle}</p>
      )}
    </div>
  );
}
